/**
 * Правая панель Activity.
 * Собирает единый UI чата: pins, историю сообщений и composer,
 * подписываясь на состояние через `useActivityChat(controller)`.
 */
import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Group,
  Modal,
  ScrollArea,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowDown, IconInfoCircle, IconBrandTelegram, IconRefresh } from '@tabler/icons-react';
import { EventsHeader, EventsPanel } from '@ayocore/platform/client/components/Events';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { getActivityEntityDescriptor } from '@ayocore/activity/client/extensions';
import type { ActivityController } from '../../controllers/ActivityController';
import { getThreadTitle } from '../utils/ActivityList.utils';
import { ActivityPinsList } from '../pins/ActivityPinsList';
import { ActivityChatMessageList } from './ActivityChatMessageList';
import { ActivityChatComposer } from './ActivityChatComposer';
import { ActivityHeaderActionsMenu } from './ActivityHeaderActionsMenu';
import { useActivityChat } from '../../hooks/useActivityChat';
import { useActivityScopeActions } from '../../hooks/useActivityScopeActions';
import { ActivityThreadTelegramModal } from './ActivityThreadTelegramModal';

type ActivityChatPanelProps = {
  controller: ActivityController;
};

/**
 * Правая панель Activity. Подписывается на chat/pins/composer через переданный root controller.
 */
export function ActivityChatPanel({ controller }: ActivityChatPanelProps) {
  const { onScopeClick, isScopeClickable } = useActivityScopeActions();
  const telegramModal = useModal(ActivityThreadTelegramModal);
  const {
    selectedThread,
    displayChatItems,
    mentionUsers,
    pins,
    chatLoading,
    chatErrorMessage,
    pinsLoading,
    pinsErrorMessage,
    sendingComment,
    replyPreview,
    scopePreset,
    activeContext,
    showEditComment,
    editingCommentText,
    isSavingComment,
    handleOpenSlashCommand,
    handleSendComment,
    cancelReply,
    handlePinClick,
    handlePinAction,
    handleMessageAction,
    handleInsertUserMention,
    handleScopePresetConsumed,
    closeActiveContext,
    closeEditComment,
    saveEditedComment,
    setEditingCommentText,
    scroll,
  } = useActivityChat(controller);
  const { viewportRef, scrollAfterSend, showScrollToBottom, scrollToBottomByUser } = scroll;
  // Инлайн-модалка (не через useModal): z-index из общего аллокатора, иначе при
  // рендере панели внутри дравера (Lead/Partner view) модалка окажется под ним.
  const editCommentModalZIndex = useSurfaceZIndex(showEditComment);

  const [reconciling, setReconciling] = useState(false);

  /** Кнопка «Обновить»: реконсиляция треда из первоисточников + тост с итогом. */
  const handleReconcileThread = async (): Promise<void> => {
    if (reconciling) return;
    setReconciling(true);
    try {
      const result = await controller.reconcileSelectedThread();
      if (!result) return;
      const inserted = result.inserted_comments + result.inserted_tasks;
      notifications.show({
        color: inserted > 0 ? 'green' : 'blue',
        title: 'Тред обновлён',
        message:
          inserted > 0
            ? `Восстановлено событий: ${inserted}`
            : 'Всё актуально — пропущенных событий нет',
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Не удалось обновить тред',
        message: error instanceof Error ? error.message : 'Неизвестная ошибка',
      });
    } finally {
      setReconciling(false);
    }
  };

  const threadScope = selectedThread
    ? {
        entity_type: selectedThread.entity_type,
        entity_uuid: selectedThread.entity_uuid,
        label: selectedThread.entity_label ?? undefined,
      }
    : null;
  const canOpenThreadEntity = isScopeClickable(threadScope);

  // Доп. мета шапки чата (например, бейджи активных сделок) —
  // из дескриптора сущности в реестре расширений Activity.
  const ChatHeaderMeta = getActivityEntityDescriptor(selectedThread?.entity_type)?.ChatHeaderMeta;

  return (
    <Box h='100%' mih={0}>
      <EventsPanel radius={0}>
        <EventsHeader
          title={selectedThread ? getThreadTitle(selectedThread) : 'Фокус'}
          radiusTop={0}
          titleNoWrap
          titleMaxWidth='50%'
          actions={
            selectedThread ? (
              <Group gap={4}>
                <ActivityHeaderActionsMenu
                  entityType={selectedThread.entity_type}
                  entityUuid={selectedThread.entity_uuid}
                />
                <Tooltip label='Обновить: восстановить пропущенные события' withArrow position='bottom'>
                  <ActionIcon
                    variant='subtle'
                    color='blue'
                    size='md'
                    aria-label='Обновить тред'
                    loading={reconciling}
                    onClick={() => {
                      void handleReconcileThread();
                    }}
                  >
                    <IconRefresh size={20} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label='Telegram-группы' withArrow position='bottom'>
                  <ActionIcon
                    variant='subtle'
                    color='blue'
                    size='md'
                    aria-label='Добавить уведомление в Telegram'
                    onClick={() => {
                      if (!selectedThread) return;
                      telegramModal.open(
                        {
                          title: 'Добавить уведомление в Telegram',
                          thread: { id: selectedThread.id, uuid: selectedThread.uuid },
                        },
                        { size: 'md' }
                      );
                    }}
                  >
                    <IconBrandTelegram size={20} />
                  </ActionIcon>
                </Tooltip>
                {canOpenThreadEntity && (
                  <ActionIcon
                    variant='subtle'
                    color='blue'
                    size='md'
                    aria-label='Открыть сущность треда'
                    onClick={() => {
                      void onScopeClick(threadScope);
                    }}
                  >
                    <IconInfoCircle size={20} />
                  </ActionIcon>
                )}
              </Group>
            ) : null
          }
        />

        {selectedThread && ChatHeaderMeta ? <ChatHeaderMeta item={selectedThread} /> : null}

        {!selectedThread ? (
          <Center h='100%'>
            <Text size='sm' c='white'>
              Выберите тред слева, чтобы посмотреть детали.
            </Text>
          </Center>
        ) : (
          <>
            {chatErrorMessage ? (
              <Alert color='red' title='Ошибка загрузки чата' m='sm'>
                {chatErrorMessage}
              </Alert>
            ) : null}
            {pinsErrorMessage ? (
              <Alert color='yellow' title='Ошибка загрузки закрепов' m='sm'>
                {pinsErrorMessage}
              </Alert>
            ) : null}

            <Box style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
              <ScrollArea
                viewportRef={viewportRef}
                flex={1}
                mih={0}
                scrollbarSize={4}
                styles={{
                  root: { minHeight: 180 },
                  viewport: {
                    paddingInlineStart: 8,
                    paddingInlineEnd: 8,
                    paddingBlockStart: 4,
                    paddingBlockEnd: 0,
                  },
                  content: {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100%',
                  },
                  scrollbar: { background: 'transparent' },
                  thumb: { background: 'rgba(0, 0, 0, 0.25)' },
                }}
              >
                <ActivityPinsList
                  pins={pins}
                  pinsLoading={pinsLoading}
                  onPinClick={handlePinClick}
                  onPinAction={handlePinAction}
                />
                <ActivityChatMessageList
                  chatItems={displayChatItems}
                  chatLoading={chatLoading}
                  onItemAction={handleMessageAction}
                  onInsertUserMention={handleInsertUserMention}
                />
              </ScrollArea>

              {showScrollToBottom ? (
                <Box
                  style={{
                    position: 'absolute',
                    right: 12,
                    bottom: 12,
                    zIndex: 3,
                    pointerEvents: 'none',
                  }}
                >
                  <Tooltip label='Вниз' withArrow position='left'>
                    <ActionIcon
                      variant='filled'
                      color='blue'
                      radius='xl'
                      size='lg'
                      aria-label='Прокрутить вниз'
                      onClick={scrollToBottomByUser}
                      style={{
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                        pointerEvents: 'auto',
                      }}
                    >
                      <IconArrowDown size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Box>
              ) : null}
            </Box>

            <ActivityChatComposer
              composerController={controller.composer}
              onSendComment={async (
                content,
                mentionedUserIds,
                mentionedUserMentions,
                scope,
                mentionedContactUuids,
                mentionedContactMentions,
                fileIds
              ) => {
                const sent = await handleSendComment(
                  content,
                  mentionedUserIds,
                  mentionedUserMentions,
                  scope,
                  mentionedContactUuids,
                  mentionedContactMentions,
                  fileIds
                );
                if (sent) {
                  scrollAfterSend();
                }
                return sent;
              }}
              sendingComment={sendingComment}
              replyPreview={replyPreview}
              onCancelReply={cancelReply}
              mentionUsers={mentionUsers}
              threadEntityType={selectedThread.entity_type}
              threadEntityUuid={selectedThread.entity_uuid}
              onOpenSlashCommand={handleOpenSlashCommand}
              scopePreset={scopePreset}
              onScopePresetConsumed={handleScopePresetConsumed}
              activeContext={activeContext}
              onCloseActiveContext={closeActiveContext}
            />
          </>
        )}
      </EventsPanel>

      <Modal
        opened={showEditComment}
        onClose={closeEditComment}
        title='Редактировать комментарий'
        size='md'
        zIndex={editCommentModalZIndex}
      >
        <Textarea
          value={editingCommentText}
          onChange={event => setEditingCommentText(event.currentTarget.value)}
          minRows={4}
          autosize
          placeholder='Текст комментария'
        />
        <Group justify='flex-end' mt='md'>
          <Button variant='default' onClick={closeEditComment}>
            Отмена
          </Button>
          <Button
            onClick={() => void saveEditedComment()}
            loading={isSavingComment}
            disabled={!editingCommentText.trim()}
          >
            Сохранить
          </Button>
        </Group>
      </Modal>
    </Box>
  );
}
